import { useCallback, useMemo } from "react";

import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { DesktopTimePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { DateTime } from "luxon";

import { TIME_FORMAT_12_HOUR } from "@waterly/utils-isomorphic";

import { DatePickerActionBar, type DatePickerActionBarProps } from "../../pickers";

import type { MetricInputProps } from "./DataEntryInput.types";
import { InputMode } from "./DataEntryInput.types";
import { loadingInputSx } from "./loadingInputSx";
import { useInputSx } from "./useInputSx";

export type TimeInputProps = MetricInputProps & {
  isLoading: boolean;
  timeZone: string;
};

export const TimeInput = ({ onChange, value, isDirty, mode, id, isLoading, timeZone }: TimeInputProps) => {
  const inputDisabled = mode === InputMode.ReadOnly || mode === InputMode.SiteEdit || isLoading;
  const inputReadOnly = inputDisabled || mode === InputMode.Loading;
  const inputSx = useInputSx({ mode, isDirty });

  const dateTimeValue = useMemo(() => {
    if (!value) {
      return null;
    }
    return DateTime.fromFormat(value, TIME_FORMAT_12_HOUR, { zone: timeZone });
  }, [timeZone, value]);

  const handleChange = useCallback(
    (date: DateTime | null) => {
      if (!date || !date?.isValid) {
        return onChange("");
      }
      onChange(date.toFormat(TIME_FORMAT_12_HOUR));
    },
    [onChange],
  );

  const handleOnClear = useCallback(() => {
    handleChange(null);
  }, [handleChange]);

  const handleSetNow = useCallback(() => {
    const now = DateTime.now().setZone(timeZone);
    handleChange(now);
  }, [handleChange, timeZone]);

  const actionBar = useCallback(
    (props: DatePickerActionBarProps) => {
      return <DatePickerActionBar {...props} onSetToday={handleSetNow} onClear={handleOnClear} />;
    },
    [handleSetNow, handleOnClear],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DesktopTimePicker
        onChange={handleChange}
        value={dateTimeValue}
        disabled={inputDisabled}
        timeSteps={{ hours: 1, minutes: 1 }}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
        }}
        closeOnSelect={false}
        ampmInClock
        slots={{
          actionBar: actionBar,
        }}
        sx={{
          ...(isLoading ? loadingInputSx : undefined),
          width: "100%",
        }}
        slotProps={{
          actionBar: {
            actions: ["clear", "cancel", "accept", "today"],
          },
          toolbar: {
            hidden: false,
          },
          textField: {
            inputProps: {
              sx: inputSx,
              readOnly: inputReadOnly,
              "data-component": "MetricInput",
              "data-id": id,
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
